import React, { useState, useEffect } from "react";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from "../../AuthContext";
import "./RoleBasedSettings.css";
import configData from "../../configure";

const RolePermissionsViewer = () => {
  const { axios, token } = useAuth();
  const [selectedRoleId, setSelectedRoleId] = useState("ADMIN");
  const [permissions, setPermissions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  const roleIds = ["STUDENT", "FACULTY", "STAFF"];
  const modules = [
    "STUDENTS",
    "FACULTY",
    "STUDENT_PROFILE",
    // "CLASS_SCHEDULE",
    "FEES",
    // "STUDENTDB",

    "NOTIFICATIONS",
    "DOCUMENTS",
    "STUDENT_PERFORMANCE"
  ];
  const actions = ["VIEW", "ADD", "EDIT", "DELETE", "UPDATE"];

  useEffect(() => {
    if (selectedRoleId) {
      fetchPermissions(selectedRoleId);
    } else {
      setPermissions([]);
    }
    setHasChanges(false);
  }, [selectedRoleId]);

  const fetchPermissions = async (roleId) => {
    try {
      const response = await axios.get(
        `${configData.GET_ROLE_PERMISSIONS}/${roleId}`,
        {
          headers: {
            accept: "application/hal+json",
            Authorization: token,
          },
        }
      );
      const data = response.data;

      // Initialize all modules with default permissions (all false)
      const allModulesMap = {};
      modules.forEach((module) => {
        allModulesMap[module] = {
          module: module,
          VIEW: false,
          ADD: false,
          EDIT: false,
          DELETE: false,
        };
      });

      // Update with actual permissions from API
      data.permissionsList.forEach((perm) => {
        if (allModulesMap[perm.module]) {
          perm.permissionActionsList.forEach((action) => {
            allModulesMap[perm.module][action] = true;
          });
        }
      });

      // Convert the map to array
      const transformedPermissions = Object.values(allModulesMap);
      setPermissions(transformedPermissions);
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setPermissions([]);
      toast.error("An error occurred while fetching permissions");
    }
  };

  const togglePermission = (moduleIndex, action) => {
    const updatedPermissions = [...permissions];
    updatedPermissions[moduleIndex][action] = !updatedPermissions[moduleIndex][action];
    setPermissions(updatedPermissions);
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!selectedRoleId) {
      toast.warning("Please select a role to save changes");
      return;
    }

    // Format permissions for API
    const permissionsList = permissions.map((perm) => {
      const permissionActionsList = [];
      actions.forEach((action) => {
        if (perm[action]) {
          permissionActionsList.push(action);
        }
      });

      // Only include modules that have at least one permission
      if (permissionActionsList.length > 0) {
        return {
          module: perm.module,
          permissionActionsList
        };
      }
      return null;
    }).filter((item) => item !== null);

    const roleData = {
      roleId: selectedRoleId,
      permissionsList: permissionsList
    };

    try {
      const response = await axios.put(
        `${configData.UPDATE_ROLE_PERMISSIONS}/${selectedRoleId}`,
        roleData,
        {
          headers: {
            accept: "application/hal+json",
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        toast.success("Role permissions updated successfully!");
        setHasChanges(false);
      } else {
        toast.error("Failed to update role permissions");
      }
    } catch (error) {
      console.error("Error updating role permissions:", error);
      toast.error("An error occurred while updating the role permissions");
    }
  };

  const filteredPermissions = permissions.filter(
    (perm) =>
      perm.module.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="rp-container">
      <div className="rp-permission-settings">
        <div className="rp-permission-header">
          <div className="rp-header-left">
            <h3 className="rp-title">Select Role</h3>
            <select
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
              className="rp-select"
            >
              <option value="">Select Role</option>
              {roleIds.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </div>
          <div className="rp-header-right">
            <div className="rp-search-bar">
              <input
                type="text"
                placeholder="Search module"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="rp-search-input"
              />
              <button className="rp-search-button">🔍</button>
            </div>
          </div>
        </div>

        {selectedRoleId ? (
          <>
            <div className="rp-table-container">
              <table className="rp-permission-table">
                <thead>
                  <tr>
                    <th>Module</th>
                    <th>VIEW</th>
                    <th>ADD</th>
                    <th>EDIT</th>
                    <th>DELETE</th>
                    <th>UPDATE</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPermissions.map((perm, index) => (
                    <tr key={index}>
                      <td>{perm.module}</td>
                      <td>
                        <input
                          type="checkbox"
                          checked={perm.VIEW}
                          onChange={() => togglePermission(index, "VIEW")}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={perm.ADD}
                          onChange={() => togglePermission(index, "ADD")}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={perm.EDIT}
                          onChange={() => togglePermission(index, "EDIT")}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={perm.DELETE}
                          onChange={() => togglePermission(index, "DELETE")}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="rp-action-buttons">
              <button
                onClick={handleSaveChanges}
                className={`rp-save-button ${hasChanges ? 'rp-save-active' : ''}`}
                disabled={!hasChanges}
              >
                Save Changes
              </button>
            </div>
          </>
        ) : (
          <p className="rp-no-permissions">Please select a role to view and edit permissions.</p>
        )}
      </div>

    </div>
  );
};

export default RolePermissionsViewer;